import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Search,
  Upload,
  Link2,
  X,
  Check,
  AlertCircle,
  Loader2,
  RefreshCw,
  Plane,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Trash2,
  Filter as FilterIcon,
  Edit3,
  Plus,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COVERAGE_TABS = [
  { id: 'all', label: 'All' },
  { id: 'has_logo', label: 'With Logo' },
  { id: 'missing_logo', label: 'Missing Logo' },
  { id: 'overridden', label: 'Overridden' },
];

const resolveLogoSrc = (a) => {
  if (!a) return '';
  // Override wins
  if (a.logo_url_override) {
    const o = a.logo_url_override;
    if (o.startsWith('/api/uploads/')) return `${BACKEND_URL}${o}`;
    if (o.startsWith('/uploads/')) return `${BACKEND_URL}/api${o}`; // legacy → new prefix
    return o;
  }
  if (a.has_logostream_logo && a.logo_url) return a.logo_url;
  return '';
};

const AirlineMaster = () => {
  const [airlines, setAirlines] = useState([]);
  const [stats, setStats] = useState({ total: 0, with_logo: 0, missing_logo: 0, overridden: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [coverage, setCoverage] = useState('missing_logo');
  const [editing, setEditing] = useState(null); // airline being edited
  const [creating, setCreating] = useState(false); // Add New modal open
  const [bulkSeeding, setBulkSeeding] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const limit = 25;

  const fetchAirlines = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/airlines`, {
        withCredentials: true,
        params: { page, limit, search, coverage },
      });
      setAirlines(res.data.airlines || []);
      setStats(res.data.stats || {});
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load airlines');
    } finally {
      setLoading(false);
    }
  }, [page, search, coverage]);

  useEffect(() => {
    fetchAirlines();
  }, [fetchAirlines]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleTabChange = (id) => {
    setCoverage(id);
    setPage(1);
  };

  const handleBulkSeed = async () => {
    if (!window.confirm(
      'This will re-probe Logostream for every airline missing a logo using alternate variants (logo, default). It may take a minute. Continue?'
    )) return;
    setBulkSeeding(true);
    setBulkResult(null);
    try {
      const res = await axios.post(`${API}/admin/airlines/bulk/seed-missing`, {}, {
        withCredentials: true,
      });
      setBulkResult(res.data);
      fetchAirlines();
    } catch (e) {
      setError(e.response?.data?.detail || 'Bulk seed failed');
    } finally {
      setBulkSeeding(false);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="airline-master-page">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Airline Master</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage IATA↔ICAO mappings and override airline tail logos for carriers Logostream
            doesn't cover.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchAirlines}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-semibold text-gray-700"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 hover:bg-black text-white text-sm font-bold shadow-sm"
            data-testid="add-airline-btn"
          >
            <Plus className="w-4 h-4" /> Add Airline
          </button>
          <button
            onClick={handleBulkSeed}
            disabled={bulkSeeding}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm font-bold shadow-sm disabled:opacity-60"
            data-testid="bulk-seed-btn"
          >
            {bulkSeeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {bulkSeeding ? 'Probing...' : 'Auto-seed Missing'}
          </button>
        </div>
      </div>

      {/* Bulk-seed result banner */}
      {bulkResult && (
        <div className="mb-4 p-4 rounded-lg bg-emerald-50 border border-emerald-200 flex items-start gap-3" data-testid="bulk-seed-result">
          <Check className="w-5 h-5 text-emerald-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-emerald-900">{bulkResult.message}</p>
            {bulkResult.stats?.details?.length > 0 && (
              <p className="text-xs text-emerald-700 mt-1">
                Recovered: {bulkResult.stats.details.slice(0, 10).map(d => `${d.icao_code} (${d.variant})`).join(', ')}
                {bulkResult.stats.details.length > 10 && ` +${bulkResult.stats.details.length - 10} more`}
              </p>
            )}
          </div>
          <button onClick={() => setBulkResult(null)} className="text-emerald-700 hover:text-emerald-900">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total Airlines" value={stats.total} />
        <StatCard label="With Logostream Logo" value={stats.with_logo} tone="emerald" />
        <StatCard label="Missing Logo" value={stats.missing_logo} tone="red" />
        <StatCard label="Overridden" value={stats.overridden} tone="indigo" />
      </div>

      {/* Tabs + search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-4 border-b border-gray-100">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg overflow-x-auto">
            {COVERAGE_TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => handleTabChange(t.id)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition ${
                  coverage === t.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid={`tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <form onSubmit={handleSearchSubmit} className="flex-1 flex gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by IATA / ICAO / name..."
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
                data-testid="search-input"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-sm font-semibold"
              data-testid="search-btn"
            >
              Search
            </button>
          </form>
          <p className="text-xs text-gray-500 whitespace-nowrap">
            {total.toLocaleString()} results · Page {page}/{totalPages}
          </p>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-600 text-sm" data-testid="error-msg">{error}</div>
          ) : airlines.length === 0 ? (
            <div className="p-12 text-center text-gray-500 text-sm" data-testid="empty-state">
              No airlines match your filters.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left w-16">Logo</th>
                  <th className="px-4 py-3 text-left">IATA</th>
                  <th className="px-4 py-3 text-left">ICAO</th>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Country</th>
                  <th className="px-4 py-3 text-left">Source</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {airlines.map((a) => {
                  const logoSrc = resolveLogoSrc(a);
                  const displayName = a.name_override || a.name || a.official_name || '(unnamed)';
                  const source = a.logo_url_override
                    ? 'overridden'
                    : a.has_logostream_logo
                    ? 'logostream'
                    : 'none';
                  return (
                    <tr key={a.icao_code || a.iata_code} className="hover:bg-gray-50" data-testid={`row-${a.icao_code || a.iata_code}`}>
                      <td className="px-4 py-2">
                        <div className="w-10 h-10 flex items-center justify-center">
                          {logoSrc ? (
                            <img
                              src={logoSrc}
                              alt={displayName}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className="w-full h-full rounded-md bg-gray-100 flex items-center justify-center text-gray-400"
                            style={{ display: logoSrc ? 'none' : 'flex' }}
                          >
                            <Plane className="w-4 h-4" />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs font-bold text-gray-900">
                        {a.iata_code || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs font-bold text-gray-700">
                        {a.icao_code || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-2">
                        <p className="font-semibold text-gray-900 truncate max-w-[260px]">{displayName}</p>
                        {a.name_override && (
                          <p className="text-[10px] text-indigo-600 truncate max-w-[260px]">
                            was: {a.name}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-600 text-xs">
                        {a.country_name || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-2">
                        <SourceBadge source={source} />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => setEditing(a)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 rounded"
                          data-testid={`edit-btn-${a.icao_code || a.iata_code}`}
                        >
                          <Edit3 className="w-3.5 h-3.5" /> Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total.toLocaleString()}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 text-xs font-semibold text-gray-700">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="next-page-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <EditAirlineModal
          airline={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            fetchAirlines();
          }}
        />
      )}

      {/* Create modal */}
      {creating && (
        <CreateAirlineModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            fetchAirlines();
          }}
        />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Subcomponents
// ────────────────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, tone = 'gray' }) => {
  const tones = {
    gray: 'bg-white border-gray-200 text-gray-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <p className="text-xs font-semibold uppercase tracking-wider opacity-70">{label}</p>
      <p className="text-2xl font-bold mt-1">{(value ?? 0).toLocaleString()}</p>
    </div>
  );
};

const SourceBadge = ({ source }) => {
  const config = {
    logostream: { label: 'Logostream', color: 'bg-emerald-100 text-emerald-700' },
    overridden: { label: 'Overridden', color: 'bg-indigo-100 text-indigo-700' },
    none: { label: 'Missing', color: 'bg-red-100 text-red-700' },
  }[source] || { label: source, color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${config.color}`}>
      {config.label}
    </span>
  );
};

const EditAirlineModal = ({ airline, onClose, onSaved }) => {
  const [iataCode, setIataCode] = useState(airline.iata_code || '');
  const [icaoCode, setIcaoCode] = useState(airline.icao_code || '');
  const [nameOverride, setNameOverride] = useState(airline.name_override || '');
  const [logoUrlOverride, setLogoUrlOverride] = useState(airline.logo_url_override || '');
  const [uploadFile, setUploadFile] = useState(null);
  const [mode, setMode] = useState(airline.logo_url_override?.startsWith('/uploads/') ? 'upload' : 'url');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState(resolveLogoSrc(airline));
  const fileInputRef = useRef(null);

  const displayName = airline.name_override || airline.name || airline.official_name || '';

  const handleFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      setErr('File too large (max 2 MB)');
      return;
    }
    setUploadFile(f);
    setErr('');
    setPreview(URL.createObjectURL(f));
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      const res = await axios.post(
        `${API}/admin/airlines/${airline.icao_code}/upload-logo`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setLogoUrlOverride(res.data.logo_url_override);
      setPreview(`${BACKEND_URL}${res.data.logo_url_override}?t=${Date.now()}`);
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      setErr(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErr('');
    try {
      const newIcao = icaoCode.trim().toUpperCase();
      const origIcao = (airline.icao_code || '').toUpperCase();
      await axios.put(
        `${API}/admin/airlines/${airline.icao_code}`,
        {
          iata_code: iataCode !== (airline.iata_code || '') ? iataCode : null,
          new_icao_code: newIcao && newIcao !== origIcao ? newIcao : null,
          name_override: nameOverride !== (airline.name_override || '') ? nameOverride : null,
          logo_url_override: mode === 'url' && logoUrlOverride !== (airline.logo_url_override || '') ? logoUrlOverride : null,
        },
        { withCredentials: true }
      );
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClearOverride = async () => {
    if (!window.confirm('Remove all overrides for this airline? It will revert to Logostream auto-resolution.')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/airlines/${airline.icao_code}/override`, { withCredentials: true });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Clear failed');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="edit-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Edit Airline</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {airline.icao_code} · {displayName}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="close-modal-btn">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Preview */}
          <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-4">
            <div className="w-20 h-20 bg-white rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden">
              {preview ? (
                <img src={preview} alt="preview" className="w-full h-full object-contain p-2" />
              ) : (
                <Plane className="w-8 h-8 text-gray-300" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Current Preview</p>
              <p className="text-sm font-bold text-gray-900 truncate mt-1">{displayName}</p>
              <p className="text-[11px] text-gray-500 truncate">
                IATA: {airline.iata_code || '—'} · ICAO: {airline.icao_code} · {airline.country_name || '—'}
              </p>
            </div>
          </div>

          {/* IATA + ICAO codes */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">IATA Code</label>
              <input
                value={iataCode}
                onChange={(e) => setIataCode(e.target.value.toUpperCase())}
                maxLength={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
                placeholder="UK"
                data-testid="iata-input"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                2-char code (Tripjack/Riya). Leave blank if unknown.
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                ICAO Code <span className="text-amber-600">*</span>
              </label>
              <input
                value={icaoCode}
                onChange={(e) => setIcaoCode(e.target.value.toUpperCase())}
                maxLength={4}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
                placeholder="VTI"
                data-testid="icao-input"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                3-4 char code (Logostream key). Changing re-probes the logo.
              </p>
            </div>
          </div>

          {/* Name override */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Name Override</label>
            <input
              value={nameOverride}
              onChange={(e) => setNameOverride(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
              placeholder={airline.name || '(unnamed)'}
              data-testid="name-override-input"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Shown to end-users instead of the ICAO official name. Leave blank to use "{airline.name}".
            </p>
          </div>

          {/* Logo override */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold text-gray-700">Logo Override</label>
              <div className="flex gap-1 bg-gray-100 p-0.5 rounded">
                <button
                  onClick={() => setMode('upload')}
                  className={`px-2.5 py-1 rounded text-[11px] font-semibold flex items-center gap-1 ${
                    mode === 'upload' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                  }`}
                  data-testid="mode-upload-btn"
                >
                  <Upload className="w-3 h-3" /> Upload
                </button>
                <button
                  onClick={() => setMode('url')}
                  className={`px-2.5 py-1 rounded text-[11px] font-semibold flex items-center gap-1 ${
                    mode === 'url' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                  }`}
                  data-testid="mode-url-btn"
                >
                  <Link2 className="w-3 h-3" /> URL
                </button>
              </div>
            </div>

            {mode === 'upload' ? (
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.svg,.jpg,.jpeg,.webp"
                  onChange={handleFilePick}
                  className="hidden"
                  id="logo-file-input"
                  data-testid="logo-file-input"
                />
                <label
                  htmlFor="logo-file-input"
                  className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-xs font-semibold"
                >
                  <Upload className="w-3.5 h-3.5" /> Choose file
                </label>
                {uploadFile && (
                  <div className="mt-3 flex items-center justify-center gap-3 text-xs text-gray-600">
                    <span className="truncate max-w-[200px]">{uploadFile.name}</span>
                    <button
                      onClick={handleUpload}
                      disabled={uploading}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-semibold disabled:opacity-60"
                      data-testid="upload-submit-btn"
                    >
                      {uploading ? 'Uploading...' : 'Upload & Save'}
                    </button>
                  </div>
                )}
                <p className="text-[10px] text-gray-400 mt-2">PNG, SVG, JPG, WebP · Max 2 MB</p>
              </div>
            ) : (
              <input
                value={logoUrlOverride}
                onChange={(e) => {
                  setLogoUrlOverride(e.target.value);
                  if (e.target.value) setPreview(e.target.value);
                }}
                placeholder="https://example.com/airline-tail.png"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
                data-testid="logo-url-input"
              />
            )}
          </div>

          {err && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2" data-testid="modal-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {err}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-gray-200 flex items-center justify-between bg-gray-50 rounded-b-2xl">
          {airline.logo_url_override || airline.name_override ? (
            <button
              onClick={handleClearOverride}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-lg"
              data-testid="clear-override-btn"
            >
              <Trash2 className="w-3.5 h-3.5" /> Clear Overrides
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg"
              data-testid="cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold shadow-sm disabled:opacity-60"
              data-testid="save-btn"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// CreateAirlineModal — for airlines missing from the master list entirely
// ────────────────────────────────────────────────────────────────────────────

const CreateAirlineModal = ({ onClose, onCreated }) => {
  const [icaoCode, setIcaoCode] = useState('');
  const [iataCode, setIataCode] = useState('');
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [countryName, setCountryName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleCreate = async () => {
    setErr('');
    const icao = icaoCode.trim().toUpperCase();
    const iata = iataCode.trim().toUpperCase();
    if (!icao || icao.length < 3 || icao.length > 4) {
      setErr('ICAO code is required (3-4 characters)');
      return;
    }
    if (!name.trim()) {
      setErr('Airline name is required');
      return;
    }
    if (iata && (iata.length < 2 || iata.length > 3)) {
      setErr('IATA code must be 2 or 3 characters if provided');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/airlines`,
        {
          icao_code: icao,
          iata_code: iata,
          name: name.trim(),
          country_code: countryCode.trim().toUpperCase(),
          country_name: countryName.trim(),
        },
        { withCredentials: true }
      );
      onCreated();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Add New Airline</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Logostream tail logo is auto-probed on save.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="create-close-btn">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                ICAO Code <span className="text-red-500">*</span>
              </label>
              <input
                value={icaoCode}
                onChange={(e) => setIcaoCode(e.target.value.toUpperCase())}
                maxLength={4}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
                placeholder="VTI"
                data-testid="create-icao-input"
              />
              <p className="text-[11px] text-gray-400 mt-1">3-4 chars, unique</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">IATA Code</label>
              <input
                value={iataCode}
                onChange={(e) => setIataCode(e.target.value.toUpperCase())}
                maxLength={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
                placeholder="UK"
                data-testid="create-iata-input"
              />
              <p className="text-[11px] text-gray-400 mt-1">2-3 chars, optional</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Airline Name <span className="text-red-500">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
              placeholder="Vistara"
              data-testid="create-name-input"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Country Code</label>
              <input
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
                maxLength={2}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
                placeholder="IN"
                data-testid="create-country-code-input"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Country Name</label>
              <input
                value={countryName}
                onChange={(e) => setCountryName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
                placeholder="India"
                data-testid="create-country-name-input"
              />
            </div>
          </div>

          {err && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2" data-testid="create-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {err}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-gray-200 flex items-center justify-end gap-2 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg"
            data-testid="create-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={saving}
            className="px-4 py-2 bg-gray-900 hover:bg-black text-white rounded-lg text-sm font-bold shadow-sm disabled:opacity-60 inline-flex items-center gap-2"
            data-testid="create-submit-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Creating...' : 'Create Airline'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AirlineMaster;
